import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import {
    globalLoader,
    handleServerValidations,
} from "../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { ItemDetails, EditItem, ListCategories } from "../../services/appMenu.services";

const EditAppMenuItem = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [oldImage, setOldImage] = useState(null);
    const [categories, setCategories] = useState([]);

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "App Menu Items", url: "/admin/app-menu-item/list" },
        { title: t("link_edit"), url: "" },
    ];

    useEffect(() => {
        ListCategories({ page: 1, per_page: 100 })
            .then((res) => {
                if (res?.data?.list) {
                    setCategories(res.data.list);
                }
            })
            .catch(console.error);
    }, []);

    const validationSchema = Yup.object().shape({
        title: Yup.string().trim().required("Title is required"),
        app_menu_category_id: Yup.number().required("Category is required"),
    });

    const formik = useFormik({
        initialValues: {
            title: "",
            slug: "",
            app_menu_category_id: "",
            image: null,
            status: 1,
            sort_order: 0,
            home_sort_order: 0,
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            globalLoader(true);
            setSubmitted(true);

            const formData = new FormData();
            formData.append("o_id", id);
            formData.append("title", values.title);
            formData.append("slug", values.slug);
            formData.append("app_menu_category_id", values.app_menu_category_id);
            formData.append("status", values.status);
            formData.append("sort_order", values.sort_order);
            formData.append("home_sort_order", values.home_sort_order);
            
            if (values.image) {
                formData.append("image", values.image);
            } else if (oldImage) {
                formData.append("image", oldImage);
            }

            EditItem(formData)
                .then((response) => {
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setTimeout(() => {
                            navigate(`/admin/app-menu-item/list`);
                        }, 1000);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setSubmitted(false);
                    }
                })
                .catch((error) => {
                    console.log("error ====> ", error);
                    globalLoader(false);
                    setSubmitted(false);
                });
        },
    });

    useEffect(() => {
        if (id) {
            globalLoader(true);
            ItemDetails(id)
                .then((response) => {
                    globalLoader(false);
                    if (response.success && response.data) {
                        const data = response.data;
                        formik.setValues({
                            title: data.title || "",
                            slug: data.slug || "",
                            app_menu_category_id: data.app_menu_category_id || "",
                            status: data.status ? 1 : 0,
                            sort_order: data.sort_order || 0,
                            home_sort_order: data.home_sort_order || 0,
                            image: null,
                        });
                        if (data.image) {
                            setOldImage(data.image);
                            setPreviewImage(
                                data.image.startsWith("http")
                                    ? data.image
                                    : `${process.env.REACT_APP_API_URL || ""}/${data.image}`
                            );
                        }
                    }
                })
                .catch((error) => {
                    console.log("Error fetching details", error);
                    globalLoader(false);
                });
        }
    }, [id]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            formik.setFieldValue("image", file);
            const reader = new FileReader();
            reader.onload = (ev) => setPreviewImage(ev.target.result);
            reader.readAsDataURL(file);
        }
    };

    const handleTitleChange = (e) => {
        const title = e.target.value;
        formik.setFieldValue("title", title);
        const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
        formik.setFieldValue("slug", slug);
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <h6 className="main-content-label mb-3">Edit Menu Item</h6>
                        <form onSubmit={formik.handleSubmit}>
                            <div className="row row-sm">
                                {/* Title */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="title" className="text-left d-flex">
                                        Title :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="title"
                                        id="title"
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter item title"
                                        onChange={handleTitleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                    />
                                    {formik.errors.title && formik.touched.title && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.title}
                                        </span>
                                    )}
                                </div>

                                {/* Slug */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="slug" className="text-left d-flex">
                                        Slug :
                                    </label>
                                    <input
                                        name="slug"
                                        id="slug"
                                        type="text"
                                        className="form-control"
                                        placeholder="Auto generated from title"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.slug}
                                    />
                                </div>

                                {/* Category */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="app_menu_category_id" className="text-left d-flex">
                                        Category :<span className="requirestar">*</span>
                                    </label>
                                    <select
                                        name="app_menu_category_id"
                                        id="app_menu_category_id"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.app_menu_category_id}
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.title}</option>
                                        ))}
                                    </select>
                                    {formik.errors.app_menu_category_id && formik.touched.app_menu_category_id && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.app_menu_category_id}
                                        </span>
                                    )}
                                </div>

                                {/* Status */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="status" className="text-left d-flex">
                                        Status :
                                    </label>
                                    <select
                                        name="status"
                                        id="status"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.status}
                                    >
                                        <option value={1}>{t("Active")}</option>
                                        <option value={0}>{t("Inactive")}</option>
                                    </select>
                                </div>

                                {/* Sort Order */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="sort_order" className="text-left d-flex">
                                        Sort Order :
                                    </label>
                                    <input
                                        name="sort_order"
                                        id="sort_order"
                                        type="number"
                                        min="0"
                                        className="form-control"
                                        placeholder="Enter sort order (e.g. 1, 2, 3)"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.sort_order}
                                    />
                                </div>

                                {/* Home Sorting */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="home_sort_order" className="text-left d-flex">
                                        Home Sorting :
                                    </label>
                                    <input
                                        name="home_sort_order"
                                        id="home_sort_order"
                                        type="number"
                                        min="0"
                                        className="form-control"
                                        placeholder="Enter home sort order (e.g. 1, 2, 3)"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.home_sort_order}
                                    />
                                </div>

                                {/* Image */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="image" className="text-left d-flex">
                                        Icon / Image :
                                    </label>
                                    <input
                                        name="image"
                                        id="image"
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                    {previewImage && (
                                        <div className="mt-2">
                                            <img
                                                src={previewImage}
                                                alt="Preview"
                                                style={{
                                                    width: "80px",
                                                    height: "80px",
                                                    objectFit: "cover",
                                                    borderRadius: "10px",
                                                    border: "1px solid #dee2e6",
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="col-md-12 mt-3">
                                    <button
                                        className="btn btn-main-primary signbtn mr-2"
                                        type="submit"
                                        disabled={submitted}
                                    >
                                        <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                        {t("btn_update")}
                                    </button>
                                    <button
                                        className="btn ripple btn-secondary"
                                        type="button"
                                        onClick={() => navigate(-1)}
                                    >
                                        <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                        {t("btn_cancel")}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditAppMenuItem;
